import { CloseOutlined } from '@ant-design/icons';
import { Button, Modal } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos, Close, LinkOutlined } from '@mui/icons-material';
import { Row } from 'antd';
import { get, find } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAllBook } from 'store/books/selectors';
import { useStory } from 'store/story/selectors';
import styled from 'styled-components';
import COLOR from 'utils/constants/color';
import { ebookConfigDinamic } from 'utils/lib/ebookHelper';
import { disableBackWrad, disableForWard, storyRedirect } from '../helper';
import BookImage from './bookImage';
import { useCurrentUser } from 'store/users/selectors';
import AssignmentsComponent from '../../../assignments';

const StoryPreview = ({
  open,
  handleClose,
  selectedItems,
  styles,
  level,
  extensionActivity = {},
}) => {
  const currentUser = useCurrentUser();
  const navigate = useNavigate();
  const backBtnRef = useRef();
  const forwardBtnRef = useRef();
  const [page, setPage] = useState('');
  const [imageCache, setImageCache] = useState(() => new Map());
  const [currentBatch, setCurrentBatch] = useState(0);
  const [preloadingBatch, setPreloadingBatch] = useState(false);

  const booksAllList = useAllBook();
  const story = useStory();

  // Memoize selectedItems.id to prevent unnecessary recalculations
  const selectedItemId = useMemo(() => selectedItems?.id, [selectedItems?.id]);

  // Memoize getSelectedItem with stable dependencies
  const getSelectedItem = useMemo(() => {
    if (!story || !selectedItemId) return null;
    return story.find((item) => item?.id === selectedItemId);
  }, [story, selectedItemId]);

  // Memoize CHAPTER_ID
  const CHAPTER_ID = useMemo(() => {
    if (!getSelectedItem) return null;
    return get(find(story, ['id', getSelectedItem.id]), 'bookId');
  }, [story, getSelectedItem]);

  // Memoize ebook data
  const data = useMemo(() => {
    if (!CHAPTER_ID || !booksAllList) return null;
    return ebookConfigDinamic(CHAPTER_ID, booksAllList);
  }, [CHAPTER_ID, booksAllList]);

  // Memoize content with stable reference
  const content = useMemo(() => data?.content || [], [data?.content]);

  // Memoize bookData with stable dependencies
  const bookData = useMemo(() => {
    if (!getSelectedItem?.levels || !level) return {};
    return (
      getSelectedItem.levels.find((item) => item?.level?.toLowerCase() === level.toLowerCase()) ||
      {}
    );
  }, [getSelectedItem?.levels, level]);

  // Set initial page only when bookData.start changes
  useEffect(() => {
    if (extensionActivity?.linkFromEbook && currentUser?.role === 'student') {
      setPage(extensionActivity?.linkFromEbook);
    } else if (bookData?.start && bookData.start !== page) {
      setPage(bookData.start);
    }
  }, [bookData?.start]); // Remove page from deps to prevent infinite loop

  // Memoize getSrcPage
  const getSrcPage = useMemo(() => {
    if (!content.length || !page) return null;
    const dataPage = content.find((x) => Number(x.pageId) === Number(page));
    return dataPage?.src || null;
  }, [content, page]);

  // Memoize currentPageIndex
  const currentPageIndex = useMemo(() => {
    if (!content.length || !page) return -1;
    return content.findIndex((item) => Number(item.pageId) === Number(page));
  }, [content, page]);

  // Memoize getCurrentBatch
  const getCurrentBatch = useMemo(() => {
    if (currentPageIndex < 0) return 0;
    return Math.floor(currentPageIndex / 5);
  }, [currentPageIndex]);

  // Memoize preloadBatch function
  const preloadBatch = useCallback(
    async (batchNumber) => {
      if (preloadingBatch || !content.length) return;

      setPreloadingBatch(true);
      const startIndex = batchNumber * 5;
      const endIndex = Math.min(startIndex + 5, content.length);
      const batchItems = content.slice(startIndex, endIndex);

      const preloadPromises = batchItems.map((item) => {
        return new Promise((resolve) => {
          // Check current cache state
          setImageCache((currentCache) => {
            if (currentCache.has(item.src)) {
              resolve(item.src);
              return currentCache;
            }

            const img = new Image();
            img.onload = () => {
              setImageCache((prev) => {
                const newCache = new Map(prev);
                newCache.set(item.src, img);
                return newCache;
              });
              resolve(item.src);
            };
            img.onerror = () => {
              console.warn(`Failed to cache image: ${item.src}`);
              resolve(item.src);
            };
            img.src = item.src;

            return currentCache;
          });
        });
      });

      await Promise.all(preloadPromises);
      setCurrentBatch(batchNumber);
      setPreloadingBatch(false);
    },
    [preloadingBatch, content],
  );

  // Optimize preloading effect
  useEffect(() => {
    if (content.length === 0) return;

    const requiredBatch = getCurrentBatch;

    // Only preload if batch has changed
    if (requiredBatch !== currentBatch && requiredBatch >= 0) {
      preloadBatch(requiredBatch);

      // Preload next batch with delay
      const totalBatches = Math.ceil(content.length / 5);
      if (requiredBatch + 1 < totalBatches) {
        const timer = setTimeout(() => preloadBatch(requiredBatch + 1), 100);
        return () => clearTimeout(timer);
      }
    }
  }, [getCurrentBatch, currentBatch, content.length]);

  // Memoize redirect function
  const redirectPage = useCallback(() => {
    handleClose();
    if (!getSelectedItem?.id || !level || !page) return;
    const url = storyRedirect(getSelectedItem.id, level.toUpperCase(), page);
    navigate(url);
  }, [handleClose, getSelectedItem?.id, level, page]);

  // Memoize page change handler
  const handlePageChange = useCallback((direction) => {
    setPage((prevPage) => {
      const currentPage = parseInt(prevPage, 10);
      return direction === 'next' ? currentPage + 1 : currentPage - 1;
    });
  }, []);

  // Memoize keyboard handler
  const checkKey = useCallback((e) => {
    const isMainBook =
      document.getElementsByClassName('main-book-popup printScreenPage').length > 0;

    e = e || window.event;
    if (e.keyCode === 37) {
      // Left arrow
      if (backBtnRef.current && !backBtnRef.current.disabled) {
        backBtnRef.current.click();
      }
    } else if (e.keyCode === 39) {
      // Right arrow
      if (forwardBtnRef.current && !forwardBtnRef.current.disabled) {
        forwardBtnRef.current.click();
      }
    }
  }, []);

  // Optimize keyboard event listener
  useEffect(() => {
    if (!open) return; // Only add listener when modal is open

    document.addEventListener('keydown', checkKey);
    return () => {
      document.removeEventListener('keydown', checkKey);
    };
  }, [open, checkKey]);

  // Memoize navigation button states
  const isBackDisabled = useMemo(
    () => disableBackWrad(page, bookData?.start),
    [page, bookData?.start],
  );

  const isForwardDisabled = useMemo(
    () => disableForWard(page, bookData?.finish),
    [page, bookData?.finish],
  );

  // Early return if not open
  if (!open) return null;

  return (
    <>
      {currentUser?.role === 'teacher' && open && (
        <StyledModal
          open={open}
          onClose={handleClose}
          className={'lessonPlanPopUp main-book-popup'}
        >
          <LearnPlanWrapper>
            <LessionPlanContainer isStudent={currentUser?.role === 'student'}>
              <div className="w-full align-center flex justify-center">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<LinkOutlined />}
                  onClick={redirectPage}
                  style={{ marginBottom: '10px' }}
                >
                  Go to Page
                </Button>
              </div>

              <CloseBtn
                onClick={() => {
                  handleClose(false);
                }}
              >
                <StyledCloseIcon />
              </CloseBtn>
              <div className="balconPreviewPrintWrapper">
                <PrintWrapper>
                  <ButtonContainer
                    ref={backBtnRef}
                    disabled={disableBackWrad(page, bookData?.start)}
                    onClick={() => handlePageChange('back')}
                    left={0}
                    className="cstmLeftArow"
                  >
                    <StyledArrowBackIos />
                  </ButtonContainer>
                  <BookImage src={getSrcPage} imageCache={imageCache} alt={`Book page ${page}`} />
                  <ButtonContainer
                    ref={forwardBtnRef}
                    disabled={disableForWard(page, bookData?.finish)}
                    onClick={() => handlePageChange('next')}
                    right={0}
                    className="cstmRghtArow"
                  >
                    <StyledArrowForwardIos />
                  </ButtonContainer>
                </PrintWrapper>
                <div className="balconPreviewPrintRight">
                  <AssignmentsComponent
                    bookId={CHAPTER_ID}
                    pageId={page}
                    extensionActivityId={extensionActivity?.id}
                    from={'extensionActivity'}
                    renderAssignments={'admin'}
                  />
                </div>
              </div>
            </LessionPlanContainer>
          </LearnPlanWrapper>
        </StyledModal>
      )}

      {currentUser?.role === 'student' && open && (
        <Wrapper
          width={styles.width}
          height={styles.height + 36}
          className="main-book-popup printScreenPage"
        >
          <CloseButton onClick={handleClose} />
          <HeaderRow
            style={{
              justifyContent: extensionActivity?.disableNavigation ? 'center' : 'space-between',
            }}
          >
            {extensionActivity?.disableNavigation ? null : (
              <ButtonContainer
                ref={backBtnRef}
                disabled={isBackDisabled}
                onClick={() => handlePageChange('back')}
                left={0}
                className="cstmLeftArow"
              >
                <StyledArrowBackIos />
              </ButtonContainer>
            )}

            <StyledGoToPageButton
              variant="contained"
              color="primary"
              startIcon={<LinkOutlined />}
              onClick={redirectPage}
            >
              Go to Page
            </StyledGoToPageButton>
            {extensionActivity?.disableNavigation ? null : (
              <ButtonContainer
                ref={forwardBtnRef}
                disabled={isForwardDisabled}
                onClick={() => handlePageChange('next')}
                right={0}
                className="cstmRghtArow"
              >
                <StyledArrowForwardIos />
              </ButtonContainer>
            )}
          </HeaderRow>
          <BookImage src={getSrcPage} imageCache={imageCache} alt={`Book page ${page}`} />
        </Wrapper>
      )}
    </>
  );
};

export default React.memo(StoryPreview);

const PrintWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50%;
`;

const StyledCloseIcon = styled(Close)`
  color: #fff;
  font-weight: bolder;
  font-size: 25px;
`;

const CloseBtn = styled.div`
  background-color: rgb(204, 80, 71);
  border: none;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  position: absolute;
  top: 10px;
  right: 10px;
  color: white;
  z-index: 1;
`;

const StyledModal = styled(Modal)`
  height: 100%;
  width: 100%;
  background-color: transparent;
  z-index: 1000 !important;
`;

const LearnPlanWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background-color: transparent;
  overflow: hidden;
`;

const LessionPlanContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  // height: auto;
  max-width: 90%;
  max-height: 90%;
  position: relative;
  padding: 20px;
  background-color: white;
  justify-content: ${(props) => (props.isStudent ? 'center' : 'flex-start')};
  align-items: start;
  @media (max-width: 1024px){
    min-height: 600px;
  }
  @media (max-width: 990px){
    min-height: 550px;
  }
  @media (max-width: 800px){
    min-height: 450px;
  }
  @media (max-width: 700px){
    min-height: 400px;
  }
  @media (max-width: 600px){
    min-height: 350px;
  }
  @media (max-width: 450px){
    min-height: 480px;
  }
`;

// Styled Components remain the same...
const CloseButton = styled(CloseOutlined)`
  position: absolute;
  top: -10px;
  right: -10px;
  cursor: pointer;
  font-size: 16px;
  color: white;
  background: red;
  padding: 5px;
  border-radius: 50%;
`;

const HeaderRow = styled(Row)`
  height: 47px;
  padding: 5px;
  justify-content: space-between;
  display: flex;
  background-color: ${COLOR.white};
  padding-right: 20px;
`;

const StyledGoToPageButton = styled(Button)`
  margin-bottom: 10px;
`;

const StyledArrowBackIos = styled(ArrowBackIos)`
  font-size: 20px;
`;

const StyledArrowForwardIos = styled(ArrowForwardIos)`
  font-size: 20px;
`;

const Wrapper = styled.div`
  position: absolute;
  top: 0px;
  width: ${(p) => p.width}px !important;
  height: ${(p) => p.height-10}px !important;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 2;
  box-shadow:
    7px -3px 26px 20px rgb(66 66 66),
    -4px -3px 26px 15px rgb(95 109 110);
`;

const ButtonContainer = styled.button`
  background-color: transparent;
  border: none;
  color: ${COLOR.black600};
  cursor: pointer;
  outline: none;
  transition: all 300ms ease;
  width: 40px;
  z-index: 1;
`;
